import {getAssetTypeDescriptor} from '@sollapay/domain/asset-types';
import {cn} from '@sollapay/ui';
import {
  FieldCombobox,
  FieldInput,
  FieldTextarea,
  FormSectionHeading,
  Icon
} from '@sollapay/ui/components';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {ControlledAddressField} from '@/features/address';
import {AiFlashWrapper} from '@/features/ai-extraction';
import {ASSET_FIELD_CONSTRAINTS, ASSET_FIELD_ICONS} from '@/features/buyers/config';
import {getNestedFieldError} from '@/features/buyers/utils';
import {
  ASSET_FIELD_GENERATORS,
  generateAssetDescription,
  withDebugSetValue
} from '@/features/debug-test-data';

import type {AiFieldAccessor} from '@/features/ai-extraction';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {AssetFieldSpec} from '@sollapay/domain/asset-types';
import type {AssetType} from '@sollapay/enums';
import type {ReactNode} from 'react';
import type {Control, FieldValues, UseFormRegister} from 'react-hook-form';

export interface AssetDescriptorFieldsAi {
  fieldAi: (field: string) => AiFieldAccessor;
  isFlashing: (field: string) => boolean;
}

export interface AssetDescriptorFieldsDebug {
  setDebugValue: DebugSetValue;
}

export interface AssetDescriptorFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors: unknown;
  /** Field path prefix — `'asset'` when the asset is nested under its own key, `''` when the
   * form's own values ARE the asset (e.g. the activation asset step). */
  namePrefix: string;
  assetType: AssetType | '' | undefined;
  allowedAssetTypes: AssetType[];
  onAssetTypeChange: (next: AssetType) => void;
  /** i18n scope for `assetType`/`assetTypePlaceholder`/`additionalDetailsTitle`. */
  i18nPrefix: string;
  /** Subtitle under "Additional Details" — omit to render the heading with no subtitle. */
  additionalDetailsDescription?: string;
  /** Omit to render plain fields with no AI wiring (e.g. the activation asset step, which isn't AI-extractable). */
  ai?: AssetDescriptorFieldsAi;
  /** Omit to skip the debug corner-badge generators (e.g. outside local/dev tooling). */
  debug?: AssetDescriptorFieldsDebug;
  /** Rendered as a trailing item in the same grid row as the type combobox (or, with no type
   * choice, alongside the descriptor fields) — e.g. the deal's price, which lives beside the
   * asset fields but isn't one of them. Mutually exclusive with `fullWidthField` — passing this
   * also makes the type combobox share its row instead of taking the full width. */
  trailingField?: ReactNode;
  /** Rendered as its own full-width row, after the type choice and before "Additional Details" —
   * e.g. the activation asset step's price, which the type choice must not share a row with. */
  fullWidthField?: ReactNode;
  /** Whether a missing/malformed value blocks this form's own submit (RHF-level `required`/
   * `pattern`/`maxLength` rules). True for a one-shot creation form; false for an autosave/draft
   * form, which must stay savable with any field left blank or not yet fixed up — there, the
   * caller surfaces its own live-computed `errors` entries for the visual hint instead (mirrors
   * `ControlledAddressField`'s `validateRequired`, which this flows into for the address field). */
  enforceFieldRules?: boolean;
}

const noopAi: AssetDescriptorFieldsAi = {
  fieldAi: () => ({aiIndicator: undefined, aiGenerating: false, onAiChange: () => {}}),
  isFlashing: () => false
};

/**
 * The asset-type choice + its type-specific descriptor fields (building/unit, address, land
 * registry triple, area, description) — shared by the buyer-creation flow's asset section and
 * the trust-account activation asset step. AI-fill and debug-generator wiring are optional
 * injection points; neither applies to the activation step.
 */
export function AssetDescriptorFields<TFieldValues extends FieldValues>({
  control,
  register,
  errors,
  namePrefix,
  assetType,
  allowedAssetTypes,
  onAssetTypeChange,
  i18nPrefix,
  additionalDetailsDescription,
  ai = noopAi,
  debug,
  trailingField,
  fullWidthField,
  enforceFieldRules = true
}: AssetDescriptorFieldsProps<TFieldValues>) {
  const {t} = useTranslation('trustAccounts');
  const {fieldAi, isFlashing} = ai;

  const buildPath = (key: string) => (namePrefix ? `${namePrefix}.${key}` : key);
  const fe = (key: string) => getNestedFieldError(errors, buildPath(key));

  const descriptor = assetType ? getAssetTypeDescriptor(assetType) : undefined;

  const assetTypeOptions = allowedAssetTypes.map(type => ({
    value: type,
    label: t(`view.buyers.assetTypes.${type}`)
  }));

  const hasAssetTypeChoice = allowedAssetTypes.length > 1;

  const renderDescriptorField = (fieldSpec: AssetFieldSpec) => {
    const key = fieldSpec.key;
    const path = buildPath(key);
    const constraint = ASSET_FIELD_CONSTRAINTS[key];

    return (
      <AiFlashWrapper key={key} field={path} fieldAi={fieldAi} isFlashing={isFlashing}>
        {aiField => {
          const cornerBadge = debug
            ? withDebugSetValue(
                aiField.aiIndicator,
                debug.setDebugValue,
                path,
                key === 'description'
                  ? () => generateAssetDescription(assetType as AssetType)
                  : ASSET_FIELD_GENERATORS[key],
                aiField.onAiChange
              )
            : aiField.aiIndicator;

          if (key === 'address') {
            return (
              <ControlledAddressField
                control={control}
                name={path as never}
                required={fieldSpec.required}
                validateRequired={enforceFieldRules && fieldSpec.required}
                label={t(`view.buyers.detail.assetFields.${fieldSpec.labelToken}`)}
                error={fe(key)}
                cornerBadge={cornerBadge}
                isLoading={aiField.aiGenerating}
                onAiChange={aiField.onAiChange}
              />
            );
          }

          const commonProps = {
            label: t(`view.buyers.detail.assetFields.${fieldSpec.labelToken}`),
            placeholder: t(`view.buyers.detail.assetFields.${fieldSpec.labelToken}Placeholder`),
            error: fe(key),
            required: fieldSpec.required || undefined,
            cornerBadge,
            isLoading: aiField.aiGenerating,
            ...register(path as never, {
              ...(enforceFieldRules && {
                required: fieldSpec.required && t('common:required'),
                ...(constraint?.pattern && {
                  pattern: {value: constraint.pattern, message: t(constraint.errorKey)}
                }),
                ...(constraint?.maxLength && {
                  maxLength: {
                    value: constraint.maxLength,
                    message: t(constraint.errorKey, {max: constraint.maxLength})
                  }
                })
              }),
              onChange: () => aiField.onAiChange()
            })
          };

          if (key === 'description') {
            return <FieldTextarea className="col-span-2" {...commonProps} />;
          }

          return (
            <FieldInput
              startAdornment={<Icon name={ASSET_FIELD_ICONS[key]} className="size-4" />}
              {...(key === 'area' && {
                inputMode: 'decimal' as const,
                endAdornment: (
                  <span className="text-sm text-fg-tertiary">
                    {t('view.buyers.detail.assetFields.areaUnit')}
                  </span>
                )
              })}
              {...commonProps}
            />
          );
        }}
      </AiFlashWrapper>
    );
  };

  const assetTypeField = (
    <AiFlashWrapper field={buildPath('assetType')} fieldAi={fieldAi} isFlashing={isFlashing}>
      {aiField => (
        <Controller
          name={buildPath('assetType') as never}
          control={control}
          rules={enforceFieldRules ? {required: t('common:required')} : undefined}
          render={({field}) => (
            <FieldCombobox
              label={t(`${i18nPrefix}.assetType`)}
              placeholder={t(`${i18nPrefix}.assetTypePlaceholder`)}
              required
              options={assetTypeOptions}
              value={field.value as string}
              onValueChange={value => {
                field.onChange(value);
                onAssetTypeChange(value as AssetType);
                aiField.onAiChange();
              }}
              error={fe('assetType')}
              cornerBadge={aiField.aiIndicator}
            />
          )}
        />
      )}
    </AiFlashWrapper>
  );

  // Alone (no trailingField beside it), the type choice takes the full row — otherwise it shares
  // a 2-column grid with whatever the caller passed.
  const showsTypeChoiceAlone = hasAssetTypeChoice && !trailingField;

  return (
    <>
      <div className={cn('grid gap-6', showsTypeChoiceAlone ? 'grid-cols-1' : 'grid-cols-2')}>
        {hasAssetTypeChoice ? assetTypeField : descriptor?.fields.map(renderDescriptorField)}
        {trailingField}
      </div>

      {fullWidthField}

      {hasAssetTypeChoice && descriptor && (
        <>
          <FormSectionHeading
            title={t(`${i18nPrefix}.additionalDetailsTitle`)}
            description={additionalDetailsDescription}
          />
          <div className="grid grid-cols-2 gap-6">
            {descriptor.fields.map(renderDescriptorField)}
          </div>
        </>
      )}
    </>
  );
}
